import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ShieldCheck, AlertCircle, CheckCircle2,
  Clock, MessageSquare, Camera, FileText, RefreshCw, X, DollarSign, Truck,
  Package, Scale, Banknote, TrendingDown, Hourglass, ListChecks,
} from "lucide-react";
import { Button } from "../../components/ui/button";

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW v2: Returns / RMA + Bilateral dispute resolution
// Route: /preview/returns-rma
//
// Design revisions per user feedback:
//   a) NOT a sidebar pillar — lives inside Orders > Delivered (after-delivery)
//   b) New reason: "Quantity mismatch — received more/less than paid/agreed"
//   c) Quality Inspection (QI) deadline — buyer cannot dispute after window closes
//   d) NO admin mediation — buyer ↔ seller direct only.
//      If unresolved + buyer can't provide proof → buyer's TRAQO score drops.
//   e) Every dispute step accepts photo + video evidence
//   f) If invoice has Allianz policy + deadline passes unresolved →
//      auto-forward to Allianz for resolution (reuses existing claims pipeline)
// ─────────────────────────────────────────────────────────────────────────────
export default function ReturnsRmaPreview() {
  const [view, setView] = useState("flow"); // flow | placement | buyer | seller | dispute | resolved | timeout

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center font-bold">T</div>
            <span className="display font-semibold">TRAQO Preview · v2</span>
          </Link>
          <div className="flex items-center gap-1 text-xs flex-wrap">
            {[
              ["flow", "1. State machine"],
              ["placement", "2. Where it lives"],
              ["buyer", "3. Buyer · Open RMA"],
              ["seller", "4. Seller · Inbox"],
              ["dispute", "5. Direct dispute"],
              ["timeout", "6. Deadline expired"],
              ["resolved", "7. Resolved"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`rma-view-${v}`}
                className={`px-3 py-1.5 rounded-full border transition-colors ${
                  view === v
                    ? "bg-foreground text-background border-foreground"
                    : "border-border text-foreground/70 hover:text-foreground"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
          Returns / RMA · v2 (revised per your 5 changes)
        </div>
        <h1 className="display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] mb-4">
          Bilateral resolution. No admin in the loop.<br />Allianz catches what falls through.
        </h1>
        <p className="text-foreground/70 max-w-3xl leading-relaxed mb-6">
          Lives inside the Orders flow (no separate sidebar pillar). Every disputed line carries a
          QI deadline so claims have a hard cut-off. If the buyer can't produce proof, their TRAQO
          score takes a hit. If the buyer is on an Allianz-covered invoice and the deadline passes
          unresolved, the case is auto-forwarded to the insurer's claims pipeline.
        </p>

        {/* What changed banner */}
        <div className="mb-10 rounded-xl border border-border bg-foreground text-background p-5">
          <div className="text-xs uppercase tracking-widest text-accent mb-2">
            What changed in v2
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 text-[13px] text-background/80">
            {[
              { l: "(a) Placement", s: "Inside Orders → Delivered drawer · NOT a separate sidebar pillar" },
              { l: "(b) New reason", s: "Quantity mismatch — received more or fewer units than paid/agreed" },
              { l: "(c) QI deadline", s: "Per-seller policy · default 7d post-delivery · enforced at API + UI" },
              { l: "(d) No admin", s: "Direct buyer↔seller · no TRAQO mediator · unresolved+no-proof = score drop" },
              { l: "(e) Multi-step proof", s: "Every reply (open, counter, escalate) accepts photos + video" },
              { l: "(f) Allianz fallback", s: "If covered + deadline passes unresolved → auto-forwarded to claims" },
            ].map((c, i) => (
              <div key={i} className="rounded-lg bg-background/[0.05] p-3 border border-background/10">
                <div className="text-[11px] uppercase tracking-widest text-accent/90 font-medium">{c.l}</div>
                <div className="mt-1 leading-snug">{c.s}</div>
              </div>
            ))}
          </div>
        </div>

        {view === "flow" && <FlowDiagram />}
        {view === "placement" && <PlacementMap />}
        {view === "buyer" && <BuyerCreate />}
        {view === "seller" && <SellerInbox />}
        {view === "dispute" && <DirectDispute />}
        {view === "timeout" && <DeadlineExpired />}
        {view === "resolved" && <ResolvedState />}

        <ImplementationNotes />

        <div className="mt-12 flex items-center justify-between border-t border-border pt-6">
          <Link to="/" className="text-sm text-foreground/60 hover:text-foreground">← Back to home</Link>
          <span className="text-xs text-foreground/50">Preview only · no data is sent</span>
        </div>
      </main>
    </div>
  );
}

// ── 1. State machine ─────────────────────────────────────────────────────────
function FlowDiagram() {
  const stages = [
    { n: "1", t: "Order delivered", who: "Carrier", desc: "QI deadline starts ticking · default 7d, configurable per seller" },
    { n: "2", t: "Buyer files RMA", who: "Buyer", desc: "From Orders drawer · attaches photos/video · picks reason + qty" },
    { n: "3", t: "Seller responds", who: "Seller", desc: "Approve / Counter / Reject — must respond before deadline halfway mark" },
    { n: "4a", t: "Settled directly", who: "Both", desc: "Either side accepts the other's offer · refund or replacement fires" },
    { n: "4b", t: "Active dispute", who: "Both", desc: "Multiple offers exchanged · each can attach more proof · countdown visible" },
    { n: "5a", t: "Deadline passes — UNRESOLVED + no Allianz", who: "—", desc: "Case auto-closes · buyer's TRAQO score drops 30-60pts (severity-weighted)", warn: true },
    { n: "5b", t: "Deadline passes — UNRESOLVED + Allianz cover", who: "Allianz", desc: "Auto-forwarded · existing claims pipeline takes over · seller paid by insurer", insurance: true },
  ];

  return (
    <div className="rounded-2xl border border-border bg-background p-6 sm:p-10">
      <div className="text-xs uppercase tracking-widest text-foreground/50 mb-1">
        State machine
      </div>
      <h2 className="display text-2xl font-semibold mb-8">
        Linear timeline · two terminal branches if deadline expires
      </h2>

      <ol className="space-y-3">
        {stages.map((s, i) => (
          <li
            key={i}
            className={`flex items-start gap-4 rounded-xl p-4 border ${
              s.warn ? "border-rose-300 bg-rose-50/40"
              : s.insurance ? "border-emerald-300 bg-emerald-50/40"
              : "border-border bg-foreground/[0.02]"
            }`}
          >
            <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-semibold shrink-0 ${
              s.warn ? "bg-rose-600 text-white"
              : s.insurance ? "bg-emerald-600 text-white"
              : "bg-foreground text-background"
            }`}>
              {s.n}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">{s.t}</span>
                <span className="text-[10px] uppercase tracking-widest text-foreground/50 bg-foreground/5 px-2 py-0.5 rounded-full">
                  {s.who}
                </span>
              </div>
              <div className="text-sm text-foreground/70">{s.desc}</div>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-8 grid sm:grid-cols-3 gap-3">
        {[
          { icon: RefreshCw, t: "Refund only", s: "Wallet credit · proportional to qty disputed" },
          { icon: Package, t: "Replacement", s: "Auto-creates a new $0 order from same warehouse" },
          { icon: TrendingDown, t: "No-proof penalty", s: "Score drops 30-60pts if buyer can't produce evidence" },
        ].map((c, i) => (
          <div key={i} className="rounded-lg border border-border p-4">
            <c.icon className="w-4 h-4 text-foreground/60 mb-2" />
            <div className="text-sm font-medium">{c.t}</div>
            <div className="text-[12px] text-foreground/65 mt-1 leading-relaxed">{c.s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 2. Placement (sidebar/drawer/orders integration) ────────────────────────
function PlacementMap() {
  return (
    <div className="rounded-2xl border border-border bg-background p-6 sm:p-10">
      <div className="text-xs uppercase tracking-widest text-foreground/50 mb-1">
        Where the feature lives
      </div>
      <h2 className="display text-2xl font-semibold mb-2">
        Inside the Orders flow · zero new sidebar entries
      </h2>
      <p className="text-foreground/65 mb-8 max-w-3xl text-sm leading-relaxed">
        No "Returns" pillar. Buyers find it in their Orders drawer on any delivered order; sellers
        see it as a new tab inside the existing Orders page. Hangs off existing routing — clean.
      </p>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Buyer side */}
        <div className="rounded-xl border-2 border-border p-5 bg-foreground/[0.02]">
          <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
            Buyer side
          </div>
          <div className="font-mono text-xs bg-foreground text-background rounded-md px-2 py-1 inline-block">
            /buyer/orders <span className="text-accent">→ row click → drawer</span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-foreground/75">
            <li className="flex gap-2"><span className="text-foreground font-semibold">·</span><span>Drawer gains a new <strong>"After delivery"</strong> section.</span></li>
            <li className="flex gap-2"><span className="text-foreground font-semibold">·</span><span>Inside it: a single "<strong>Open dispute</strong>" button if QI deadline still active, or a greyed "Window closed" pill if expired.</span></li>
            <li className="flex gap-2"><span className="text-foreground font-semibold">·</span><span>Clicking opens an inline form (no new page) with line-by-line dispute selection.</span></li>
            <li className="flex gap-2"><span className="text-foreground font-semibold">·</span><span>Live disputes show a stacked card under the order summary with countdown + status.</span></li>
          </ul>
        </div>

        {/* Seller side */}
        <div className="rounded-xl border-2 border-border p-5 bg-foreground/[0.02]">
          <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
            Seller side
          </div>
          <div className="font-mono text-xs bg-foreground text-background rounded-md px-2 py-1 inline-block">
            /seller/orders <span className="text-accent">→ stage tab "Disputes"</span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-foreground/75">
            <li className="flex gap-2"><span className="text-foreground font-semibold">·</span><span>Existing 7-stage tab strip gets an 8th tab: "<strong>Disputes</strong>" — sits between Delivered and All.</span></li>
            <li className="flex gap-2"><span className="text-foreground font-semibold">·</span><span>Tab badge shows the count needing seller response (red when any are within 24h of deadline).</span></li>
            <li className="flex gap-2"><span className="text-foreground font-semibold">·</span><span>Same scannable table+drawer pattern as the rest of /seller/orders.</span></li>
            <li className="flex gap-2"><span className="text-foreground font-semibold">·</span><span>Drawer has its own conversation-style timeline with proof attachments per turn.</span></li>
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-dashed border-border p-4 text-sm text-foreground/70 bg-foreground/[0.02]">
        <strong className="text-foreground">Settings:</strong> a new "Returns &amp; QI" section under
        <code className="text-xs bg-foreground/10 mx-1 px-1 py-0.5 rounded">/seller/settings → Operations</code>
        for the seller to set their QI window (3-30 days), per-reason auto-approve rules, and
        Allianz-fallback toggle (default ON if any active cover exists).
      </div>
    </div>
  );
}

// ── 3. Buyer create RMA ──────────────────────────────────────────────────────
function BuyerCreate() {
  return (
    <div className="grid lg:grid-cols-[1fr_400px] gap-6 items-start">
      <div className="rounded-2xl border border-border bg-background p-6 sm:p-8">
        <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
          /buyer/orders → drawer · "After delivery"
        </div>
        <h2 className="display text-2xl font-semibold mb-1">Open dispute · PO #4421</h2>
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 font-medium">
            <Hourglass className="w-3 h-3" />
            QI deadline · 5d 4h remaining
          </span>
          <span className="text-foreground/55">Delivered Feb 28 · seller's window: 7 days</span>
        </div>

        <div className="mt-5 space-y-4">
          {/* Order summary */}
          <div className="rounded-xl border border-border p-4 bg-foreground/[0.02]">
            <div className="text-xs text-foreground/50 mb-1">From order</div>
            <div className="text-sm font-medium">GlowCo Beauty · $3,420 · 6 lines · Net-30 · invoice paid</div>
          </div>

          {/* Reason — note the new "quantity mismatch" option */}
          <div>
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">Reason</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { l: "Damaged in transit" },
                { l: "Wrong item received" },
                { l: "Quality below spec" },
                { l: "Quantity mismatch — received more or fewer units", sel: true, isNew: true },
                { l: "Expired / near-expiry" },
                { l: "Other" },
              ].map((r, i) => (
                <button key={i} className={`text-left text-sm rounded-lg border p-3 transition ${
                  r.sel ? "border-foreground bg-accent/5" : "border-border hover:border-foreground/40"
                }`}>
                  <div className="flex items-center gap-2">
                    {r.l}
                    {r.isNew && <span className="text-[9px] uppercase tracking-widest bg-foreground text-background px-1.5 py-0.5 rounded-full">NEW</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity mismatch sub-form (visible because that reason is selected) */}
          <div className="rounded-xl border-2 border-foreground/15 bg-accent/[0.06] p-4">
            <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">
              Quantity reconciliation
            </div>
            <div className="space-y-2">
              {[
                { sku: "Hydrating Serum 30ml", paid: 24, recv: 18, diff: -6 },
                { sku: "Vitamin C Cream 50ml", paid: 12, recv: 12, diff: 0 },
                { sku: "SPF 50 Sunscreen", paid: 36, recv: 38, diff: +2 },
              ].map((l, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2 text-sm">
                  <div className="col-span-5 font-medium">{l.sku}</div>
                  <div className="col-span-2 text-center text-foreground/60">Paid {l.paid}</div>
                  <div className="col-span-2 text-center">
                    <input type="number" defaultValue={l.recv} className="w-14 border border-border rounded px-2 py-1 text-right text-xs" />
                    <div className="text-[10px] text-foreground/55 mt-0.5">Received</div>
                  </div>
                  <div className={`col-span-3 text-right text-xs font-semibold ${
                    l.diff < 0 ? "text-rose-700" : l.diff > 0 ? "text-emerald-700" : "text-foreground/40"
                  }`}>
                    {l.diff === 0 ? "OK" : l.diff > 0 ? `+${l.diff} extra` : `${l.diff} short`}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-border text-xs text-foreground/65">
              Net adjustment: <strong className="text-foreground">−4 units · −$84</strong> (short by 6 serum, +2 sunscreen extra)
            </div>
          </div>

          {/* Photos + video required */}
          <div>
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
              Evidence (required)
            </div>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center text-sm text-foreground/60">
              <Camera className="w-5 h-5 mx-auto mb-1.5 text-foreground/40" />
              Drop photos or up to 1 short video (max 60s)
              <div className="text-[11px] text-foreground/45 mt-1">
                ⚠ Without evidence, this dispute will fail and may reduce your TRAQO Score by 30-60 points.
              </div>
            </div>
          </div>

          {/* Preferred outcome */}
          <div>
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
              Preferred resolution
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { i: RefreshCw, l: "Refund", d: "Wallet credit", sel: true },
                { i: Package, l: "Reship missing", d: "Send the 6 missing units" },
                { i: DollarSign, l: "Partial credit", d: "Adjust invoice & keep" },
              ].map((o, i) => (
                <button key={i} className={`rounded-lg border p-3 text-left transition ${
                  o.sel ? "border-foreground bg-accent/5" : "border-border hover:border-foreground/40"
                }`}>
                  <o.i className="w-4 h-4 text-foreground/60 mb-1" />
                  <div className="text-sm font-medium">{o.l}</div>
                  <div className="text-[11px] text-foreground/55">{o.d}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 flex gap-3">
            <Button variant="ghost" className="rounded-full h-11 px-5">Cancel</Button>
            <Button className="flex-1 rounded-full h-11 bg-foreground text-background hover:bg-foreground/90">
              Submit dispute · request $84 credit
            </Button>
          </div>
        </div>
      </div>

      <aside className="rounded-2xl border border-border bg-foreground/[0.02] p-5 sticky top-24">
        <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
          Annotations
        </div>
        <ul className="space-y-3 text-sm text-foreground/70">
          <li className="flex gap-2"><span className="text-foreground font-semibold">①</span>QI countdown sits at the top of the form — buyer can never miss it.</li>
          <li className="flex gap-2"><span className="text-foreground font-semibold">②</span>"Quantity mismatch" reason has its own per-line reconciliation grid (more <em>or</em> less than ordered).</li>
          <li className="flex gap-2"><span className="text-foreground font-semibold">③</span>Evidence isn't optional — explicit warning that no-proof disputes hurt the buyer's score.</li>
          <li className="flex gap-2"><span className="text-foreground font-semibold">④</span>Submit copy shows the live dollar magnitude so buyer fully understands what they're asking for.</li>
        </ul>
      </aside>
    </div>
  );
}

// ── 4. Seller inbox (now "Disputes" tab inside /seller/orders) ──────────────
function SellerInbox() {
  const rmas = [
    { id: "DSP-218", buyer: "Apex Wholesale LLC", reason: "Quantity mismatch (-6)", value: "$84", deadline: "5d 4h", state: "new" },
    { id: "DSP-217", buyer: "Boutique Forty", reason: "Wrong item", value: "$540", deadline: "1d 8h", state: "buyer-replied", urgent: true },
    { id: "DSP-216", buyer: "Northstar Co.", reason: "Quality below spec", value: "$420", deadline: "12h", state: "in-dispute", urgent: true },
    { id: "DSP-215", buyer: "Pure Skin Hub", reason: "Damaged in transit", value: "$312", deadline: "expired", state: "auto-allianz" },
    { id: "DSP-214", buyer: "—", reason: "Quality (no proof)", value: "$210", deadline: "expired", state: "auto-closed" },
  ];
  const tone = (s) =>
    s === "new" ? "bg-amber-100 text-amber-800" :
    s === "buyer-replied" ? "bg-blue-100 text-blue-800" :
    s === "in-dispute" ? "bg-rose-100 text-rose-800" :
    s === "auto-allianz" ? "bg-emerald-100 text-emerald-800" :
    s === "auto-closed" ? "bg-foreground/10 text-foreground/55" :
    "bg-foreground/10 text-foreground/70";

  return (
    <div className="rounded-2xl border border-border bg-background p-6">
      <div className="text-xs uppercase tracking-widest text-foreground/50">/seller/orders</div>
      <h2 className="display text-2xl font-semibold mt-1 mb-1">Orders</h2>

      {/* Existing tab strip + new "Disputes" tab */}
      <div className="flex items-center gap-1.5 mt-5 mb-5 text-xs flex-wrap">
        {[
          { l: "All orders" },
          { l: "Awaiting confirm" },
          { l: "Awaiting payment" },
          { l: "Preparing" },
          { l: "Ready for pickup" },
          { l: "In transit" },
          { l: "Delivered" },
          { l: "Disputes", count: 3, isNew: true, sel: true },
        ].map((t, i) => (
          <button key={i} className={`px-3 py-1.5 rounded-full border ${
            t.sel ? "bg-foreground text-background border-foreground" : "border-border text-foreground/65"
          }`}>
            {t.l}
            {t.count != null && (
              <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-semibold ${
                t.sel ? "bg-rose-500 text-white" : "bg-foreground/10 text-foreground/70"
              }`}>{t.count}</span>
            )}
            {t.isNew && <span className="ml-1.5 text-[9px] uppercase tracking-widest bg-foreground text-background px-1.5 py-0.5 rounded-full">NEW</span>}
          </button>
        ))}
      </div>

      {/* KPI tiles for the disputes tab */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { l: "Open disputes", v: "3", sub: "$1,044 contested" },
          { l: "Within 24h of deadline", v: "2", sub: "act fast", warn: true },
          { l: "Auto-closed (no proof)", v: "1", sub: "buyer score -38pts" },
          { l: "Forwarded to Allianz", v: "1", sub: "$312 covered" },
        ].map((k, i) => (
          <div key={i} className={`rounded-lg border p-3.5 ${k.warn ? "border-rose-300 bg-rose-50/40" : "border-border"}`}>
            <div className="text-[10px] uppercase tracking-widest text-foreground/50">{k.l}</div>
            <div className="display text-2xl font-semibold mt-1">{k.v}</div>
            <div className={`text-[11px] mt-0.5 ${k.warn ? "text-rose-700" : "text-foreground/55"}`}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-foreground/[0.03] text-[11px] uppercase tracking-widest text-foreground/60">
            <tr>
              <th className="text-left px-4 py-2.5">Dispute</th>
              <th className="text-left px-4 py-2.5">Buyer</th>
              <th className="text-left px-4 py-2.5">Reason</th>
              <th className="text-right px-4 py-2.5">Value</th>
              <th className="text-left px-4 py-2.5">Deadline</th>
              <th className="text-left px-4 py-2.5">State</th>
            </tr>
          </thead>
          <tbody>
            {rmas.map((r, i) => (
              <tr key={r.id} className={`border-t border-border ${r.urgent ? "bg-rose-50/30" : ""} hover:bg-foreground/[0.02]`}>
                <td className="px-4 py-3 font-mono text-xs">{r.id}</td>
                <td className="px-4 py-3">{r.buyer}</td>
                <td className="px-4 py-3 text-foreground/75">{r.reason}</td>
                <td className="px-4 py-3 text-right font-medium">{r.value}</td>
                <td className={`px-4 py-3 text-xs ${
                  r.deadline === "expired" ? "text-foreground/55"
                  : r.urgent ? "text-rose-700 font-medium" : "text-foreground/65"
                }`}>{r.deadline === "expired" ? "—" : r.deadline}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${tone(r.state)}`}>
                    {r.state.replace("-", " ")}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 5. Direct dispute (no admin) ─────────────────────────────────────────────
function DirectDispute() {
  return (
    <div className="rounded-2xl border-2 border-rose-300 bg-rose-50/30 p-6 sm:p-8">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-rose-700">
          <AlertCircle className="w-3 h-3" />
          Active dispute · DSP-216 · BILATERAL ONLY
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 text-white px-3 py-1 text-xs font-medium">
          <Hourglass className="w-3 h-3" />
          12h to deadline
        </div>
      </div>
      <h2 className="display text-2xl font-semibold mb-2">
        Buyer ↔ Seller working it out · no TRAQO admin in the loop
      </h2>
      <p className="text-sm text-foreground/65 mb-6 max-w-3xl">
        This is the new direct-resolution model. Either side can open as many counter-offers as
        they want, attaching new proof at every turn. If the deadline expires unresolved → either
        Allianz takes over (if covered) or the case auto-closes and the buyer's score drops.
      </p>

      {/* Conversation timeline with proof on every turn */}
      <div className="rounded-xl border border-border bg-background overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-foreground/55">
          Bilateral timeline · every reply can attach images + video
        </div>
        <div className="p-5 space-y-4">
          {/* Buyer turn 1 */}
          <DisputeTurn
            who="buyer"
            name="Apex Wholesale LLC"
            time="Mar 1, 14:22"
            text='"All 12 units arrived with broken seals. Cannot resell. Demanding full $420 refund."'
            proof={["12 photos", "1 video"]}
          />
          {/* Seller turn 1 */}
          <DisputeTurn
            who="seller"
            name="GlowCo Beauty"
            time="Mar 2, 09:10"
            text='"Sealed and packed correctly per pack video. Carrier scan shows handling damage on leg 3. Counter-offering 30% credit ($126) — file the rest with the carrier."'
            proof={["Pack video", "Carrier POD"]}
          />
          {/* Buyer turn 2 */}
          <DisputeTurn
            who="buyer"
            name="Apex Wholesale LLC"
            time="Mar 2, 11:30"
            text='"Reject. Carrier already disclaimed. Uploading additional photos of the broken pallet wrap inside our DC."'
            proof={["8 more photos"]}
          />
          {/* Seller turn 2 */}
          <DisputeTurn
            who="seller"
            name="GlowCo Beauty"
            time="Mar 3, 16:45"
            text='"Acknowledged. Best I can do is 60% ($252) + ship 6 replacements next week."'
            proof={[]}
          />
          {/* Pending buyer reply */}
          <div className="rounded-xl border-2 border-dashed border-border bg-foreground/[0.02] p-4 text-center text-sm text-foreground/55">
            Awaiting buyer reply · 12h to deadline
          </div>
        </div>

        {/* Action bar */}
        <div className="border-t border-border bg-foreground/[0.02] p-4">
          <div className="flex items-center gap-2 mb-3 text-xs text-foreground/60">
            <Camera className="w-3.5 h-3.5" />
            Attach photos / video to your next reply
          </div>
          <div className="flex flex-wrap gap-2">
            <Button className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700 h-10 px-4 text-sm">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />Accept seller's 60%
            </Button>
            <Button variant="outline" className="rounded-full h-10 px-4 text-sm">
              <DollarSign className="w-4 h-4 mr-1.5" />Counter-offer
            </Button>
            <Button variant="outline" className="rounded-full h-10 px-4 text-sm">
              <Camera className="w-4 h-4 mr-1.5" />Add more proof
            </Button>
            <div className="ml-auto text-[11px] text-foreground/50 self-center max-w-xs text-right">
              ⓘ TRAQO does not mediate. If unresolved by deadline + no policy → score drops 30-60pts.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DisputeTurn({ who, name, time, text, proof }) {
  const isBuyer = who === "buyer";
  return (
    <div className={`flex ${isBuyer ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[78%] rounded-2xl px-4 py-3 ${
        isBuyer ? "bg-white border border-border rounded-tl-sm" : "bg-foreground text-background rounded-tr-sm"
      }`}>
        <div className={`flex items-center gap-2 text-[11px] mb-1 ${isBuyer ? "text-foreground/55" : "text-background/60"}`}>
          <span className="font-medium">{name}</span>
          <span>·</span>
          <span>{time}</span>
        </div>
        <div className={`text-sm leading-relaxed ${isBuyer ? "text-foreground/85" : "text-background/95"}`}>{text}</div>
        {proof.length > 0 && (
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {proof.map((p, i) => (
              <span key={i} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                isBuyer ? "bg-foreground/5 text-foreground/65" : "bg-background/15 text-background/80"
              }`}>
                <Camera className="w-2.5 h-2.5" />{p}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 6. Deadline expired (the two terminal branches) ─────────────────────────
function DeadlineExpired() {
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      {/* Branch 1: no Allianz, score drops */}
      <div className="rounded-2xl border-2 border-rose-300 bg-rose-50/30 p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-rose-700 mb-2">
          <TrendingDown className="w-3 h-3" />
          Outcome A · No Allianz coverage
        </div>
        <h3 className="display text-xl font-semibold mb-3">
          Auto-closed · buyer score −38 points
        </h3>
        <p className="text-sm text-foreground/70 mb-4 leading-relaxed">
          Buyer couldn't produce sufficient proof for the quality claim and the QI deadline expired
          without resolution. Case auto-closes; the score-engine logs a no-proof event that lowers
          the buyer's TRAQO Score by 30-60 points (severity-weighted by disputed dollar value).
        </p>
        <div className="rounded-lg bg-background border border-border p-4 space-y-2.5 text-sm">
          <div className="flex justify-between"><span className="text-foreground/55">Disputed amount</span><span className="font-medium">$210</span></div>
          <div className="flex justify-between"><span className="text-foreground/55">Refund issued</span><span className="font-medium">$0</span></div>
          <div className="flex justify-between"><span className="text-foreground/55">Score change</span><span className="font-medium text-rose-700">−38 pts</span></div>
          <div className="flex justify-between"><span className="text-foreground/55">New score band</span><span className="font-medium">Medium → Low</span></div>
          <div className="flex justify-between border-t border-border pt-2"><span className="text-foreground/55">Future credit limit</span><span className="font-medium">$48k → $32k</span></div>
        </div>
        <div className="mt-3 text-[11px] text-foreground/55">
          Buyer is notified in-app + email. Score history shows the event with full context for
          rebuild / appeal in the future.
        </div>
      </div>

      {/* Branch 2: Allianz takes over */}
      <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/30 p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 mb-2">
          <ShieldCheck className="w-3 h-3" />
          Outcome B · Allianz cover active
        </div>
        <h3 className="display text-xl font-semibold mb-3">
          Auto-forwarded to Allianz · seller paid by insurer
        </h3>
        <p className="text-sm text-foreground/70 mb-4 leading-relaxed">
          The underlying invoice was insured under the seller's Allianz Trade cover. Deadline
          expired → TRAQO automatically files a claim with the existing claims pipeline and the
          dispute thread becomes the supporting evidence pack.
        </p>
        <div className="rounded-lg bg-background border border-border p-4 space-y-2.5 text-sm">
          <div className="flex justify-between"><span className="text-foreground/55">Allianz claim</span><span className="font-medium">ALZ-2026-0218</span></div>
          <div className="flex justify-between"><span className="text-foreground/55">Status</span><span className="font-medium text-blue-700">Investigating</span></div>
          <div className="flex justify-between"><span className="text-foreground/55">Evidence forwarded</span><span className="font-medium">12 photos · 1 video · POD</span></div>
          <div className="flex justify-between"><span className="text-foreground/55">Expected payout</span><span className="font-medium">8-12 days</span></div>
          <div className="flex justify-between border-t border-border pt-2"><span className="text-foreground/55">Seller wallet</span><span className="font-medium text-emerald-700">$420 reserved</span></div>
        </div>
        <div className="mt-3 text-[11px] text-foreground/55">
          Both sides notified. Buyer's score is unchanged in this branch — Allianz handles the
          financial side; we don't double-penalize.
        </div>
      </div>
    </div>
  );
}

// ── 7. Resolved ──────────────────────────────────────────────────────────────
function ResolvedState() {
  return (
    <div className="rounded-2xl border border-border bg-background p-6 sm:p-8">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 mb-2">
        <CheckCircle2 className="w-3 h-3" />
        Resolved bilaterally · DSP-218
      </div>
      <h2 className="display text-2xl font-semibold mb-2">
        Settled in 2h 14m · no admin · no insurer
      </h2>
      <p className="text-sm text-foreground/65 mb-6 max-w-3xl">
        Quantity-mismatch dispute — buyer received 6 fewer serums than paid for. Seller saw the
        receiving photos, accepted, and TRAQO auto-issued a $84 wallet credit. Both sides
        notified; full audit trail on the order timeline.
      </p>
      <div className="grid sm:grid-cols-3 gap-3">
        {[
          { i: Banknote, t: "Buyer wallet", s: "+$84 credit · usable on any future TRAQO order" },
          { i: ListChecks, t: "Score impact", s: "+2 pts to buyer (clean, fast resolution)" },
          { i: FileText, t: "Audit trail", s: "Permanent on order timeline · proof preserved 7y" },
        ].map((c, i) => (
          <div key={i} className="rounded-lg border border-border p-4">
            <c.i className="w-4 h-4 text-foreground/60 mb-2" />
            <div className="text-sm font-medium">{c.t}</div>
            <div className="text-[12px] text-foreground/65 mt-1 leading-relaxed">{c.s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Implementation notes ─────────────────────────────────────────────────────
function ImplementationNotes() {
  return (
    <section className="mt-12 rounded-2xl border border-foreground bg-accent/[0.06] p-6 sm:p-8">
      <div className="text-xs uppercase tracking-widest text-foreground/60 mb-2">
        Implementation plan
      </div>
      <h2 className="display text-2xl font-semibold mb-4">
        ~5 backend endpoints · 0 new pages · 1 new tab · 0 new vendors
      </h2>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">Backend</div>
          <ul className="space-y-1.5 text-sm text-foreground/75">
            <li>• <code className="text-xs">POST /api/orders/{"{oid}"}/disputes</code> · open</li>
            <li>• <code className="text-xs">POST /api/disputes/{"{id}"}/reply</code> · counter / accept / add proof</li>
            <li>• <code className="text-xs">POST /api/disputes/{"{id}"}/upload</code> · multipart photos/video</li>
            <li>• <code className="text-xs">GET /api/disputes</code> · scoped by role</li>
            <li>• Background sweeper · checks deadlines hourly · routes to Allianz or score-drop</li>
            <li>• Settings: <code className="text-xs">qi_window_days</code>, auto-approve rules, allianz fallback toggle</li>
            <li>• Score-engine hook · subtract 30-60 pts on no-proof close</li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">Frontend</div>
          <ul className="space-y-1.5 text-sm text-foreground/75">
            <li>• Buyer · <code className="text-xs">/buyer/orders</code> drawer gains "After delivery" section + dispute form</li>
            <li>• Seller · <code className="text-xs">/seller/orders</code> gains 8th tab "Disputes"</li>
            <li>• Bilateral timeline component (reused by both sides)</li>
            <li>• File-upload helper (object storage — MUST call <code className="text-xs">integration_playbook_expert_v2</code> for the playbook before coding)</li>
            <li>• Notifications hook into existing bell + email</li>
            <li>• Settings panel for QI window + Allianz toggle</li>
          </ul>
        </div>
      </div>

      <div className="mt-5 rounded-lg bg-background/70 border border-border p-4 text-sm text-foreground/70 leading-relaxed">
        <strong className="text-foreground">Estimated build:</strong> 1 turn for backend + tests; 1
        turn for the buyer drawer + seller tab; 0.5 turn for the deadline sweeper + Allianz
        forwarder. <strong>Object Storage is the critical-path dependency</strong> — it's currently
        a deferred P2 item, so we need to call the integration agent for that playbook before
        starting the file-upload work.
      </div>
    </section>
  );
}
